import {
  Box,
  Button,
  Grid,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { SlideAnimationContext } from "contexts/SlideAnimation";
import { UserIntegrationsContext } from "contexts/UserIntegrations";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  OrganizationVaProfile,
  VAStatus,
  VaOrganizationProfileStatus,
} from "schemas/dashboard";
import { routes } from "schemas/routes";

import ConnectGmailDialog from "components/ConnectGmailDialog";
import { BENTO_VA_ONBOARDING_CALENDLY_LINK } from "constants/influencer";
import { getSource, trackEvent } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";
import { useTracking } from "utils/useTracking";

import { SaveOrgVaProfileFunc } from "../TemplatesPick/schema";
import FAQ from "./FAQ";
import styles from "./styles";

interface Props {
  boughtTemplates: boolean;
  bookedCall: boolean;
  saveOrgVaProfile: SaveOrgVaProfileFunc;
  organizationVaProfile: OrganizationVaProfile | null;
  loading: boolean;
}

const SelfServeVaComplete = ({
  boughtTemplates,
  bookedCall,
  organizationVaProfile,
  saveOrgVaProfile,
  loading,
}: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const slideContext = useContext(SlideAnimationContext);
  const { trackVaStep } = useTracking();
  const source = getSource();

  const { currentOrg, setOrganizationVaProfile, setCurrentOrg } = useContext(
    OrganizationUserContext,
  );
  const { emailHealth, fetchIntegrationHealth, userIntegration, scopes } =
    useContext(UserIntegrationsContext);
  const { userIntegrations } = useContext(OrganizationUserContext);
  const [activeStep, setActiveStep] = useState(0);
  const [userIntegrationDialogState, setUserIntegrationDialogState] =
    useState(false);

  const handleBookCall = () => {
    trackEvent("VA Onboarding Flow - Book Call Button Clicked");
  };

  const changeOnboardingStatusToComplete = async () => {
    if (
      organizationVaProfile?.status !== VaOrganizationProfileStatus.completed
    ) {
      const updatedOrganizationVaProfile = {
        status: VaOrganizationProfileStatus.completed,
      };
      setOrganizationVaProfile((prev) => {
        if (!prev) return null;
        const copy = makeDeepCopy(prev);
        copy.status = VaOrganizationProfileStatus.completed;
        return copy;
      });
      if (!boughtTemplates) {
        setCurrentOrg((prevOrg) =>
          prevOrg ? { ...prevOrg, vaStatus: VAStatus.approved } : null,
        );
      }
      await saveOrgVaProfile(updatedOrganizationVaProfile);
    }
  };

  const handleNext = async () => {
    if (slideContext) {
      await slideContext.slideOutUp();
      slideContext.slideInUp();
    }
    trackVaStep(location.pathname, source);
    navigate(`/${routes.virtualAssistant}${location.search}`, {
      replace: true,
    });
  };

  const steps = [
    {
      label: "Connect your Gmail Account and provide Read + Write Access",
      description: `Providing access to your Gmail account allows Bento's virtual assistant to send emails on your behalf, automate follow-ups, improve your response rates and send you reminders.`,
    },
    {
      label: "You're all set! 💯",
      description: <></>,
    },
  ];

  const connectToGmail = () => {
    trackEvent("VA Onboarding Flow - Connect to Gmail Button Clicked");
    setUserIntegrationDialogState(true);
  };

  useEffect(() => {
    if (loading) {
      return;
    } else if (emailHealth) {
      changeOnboardingStatusToComplete();
      setActiveStep(2);
    } else {
      setActiveStep(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailHealth, loading]);

  useEffect(() => {
    if (currentOrg?.id) {
      fetchIntegrationHealth(currentOrg.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  const onEmailConnected = async () => {
    setUserIntegrationDialogState(false);
    setActiveStep(1);
  };

  const renderFinalStep = () => {
    if (bookedCall) {
      return "Use the button below to schedule your onboarding call";
    } else if (boughtTemplates) {
      return (
        <>
          Bento's Team will now begin writing your pitch templates. You will
          receive an email in about a week to review your templates to approve
          them. Once you approved the templates, emails will begin scheduling{" "}
          <strong>by next Monday</strong>.
        </>
      );
    } else {
      return (
        <>
          Bento Team's will now begin settings up your account. Emails will
          begin scheduling <strong>by next Monday</strong>. You will be able to
          see them in your <strong>Inbox &#8250; Scheduled</strong> folder. You
          will receive an email when the first emails have been scheduled.{" "}
        </>
      );
    }
  };

  return (
    <>
      <Typography variant="h3" gutterBottom sx={{ textAlign: "center", mt: 0 }}>
        {emailHealth ? "Onboarding is now complete" : "You're almost there!"}
      </Typography>

      <Stepper activeStep={activeStep} orientation="vertical">
        {steps.map((step, index) => (
          <Step key={step.label}>
            <StepLabel>{step.label}</StepLabel>
            <StepContent>
              <Typography sx={styles.description}>
                {step.description}
              </Typography>

              {activeStep === 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={connectToGmail}
                  size="small"
                  disableElevation
                  sx={{ mr: 1, mt: 1 }}
                >
                  Connect
                </Button>
              )}
            </StepContent>
          </Step>
        ))}
      </Stepper>

      {activeStep > 0 && (
        <Typography sx={[styles.description, { ml: 4 }]}>
          {renderFinalStep()}
        </Typography>
      )}

      {bookedCall && activeStep > 0 && (
        <Box sx={{ ml: 4, mt: 2 }}>
          <Button
            onClick={handleBookCall}
            target="_blank"
            href={BENTO_VA_ONBOARDING_CALENDLY_LINK}
            variant="contained"
            size="small"
            disableElevation
          >
            Book Call
          </Button>
        </Box>
      )}

      {activeStep > 0 && <FAQ boughtTemplates={boughtTemplates} />}
      {emailHealth && (
        <Grid container justifyContent={"center"} sx={{ mt: 4 }}>
          <Button onClick={handleNext}>
            Go to VA Dashboard{" "}
            <Box
              component="i"
              className="fa-regular fa-arrow-right"
              sx={{ ml: 1 }}
            />
          </Button>
        </Grid>
      )}

      {/* Create a custom gmail connect in order to perform action automatically
        after connecting */}
      <ConnectGmailDialog
        openIntegrationDialog={userIntegrationDialogState}
        setOpenIntegrationDialog={(newOpen: boolean) =>
          setUserIntegrationDialogState(newOpen)
        }
        userIntegrations={userIntegrations}
        userIntegration={userIntegration}
        scopes={scopes}
        emailHealth={emailHealth}
        onSuccessAction={onEmailConnected}
        title="Connect your Gmail account"
      />
    </>
  );
};

export default SelfServeVaComplete;
